import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Mail, Ticket, ListTodo, Activity, BookUser, BookOpen, StickyNote, CornerDownLeft, Loader2, X } from "lucide-react";
import { api } from "../lib/api";
import { useCompany } from "../context/CompanyContext";

const ICONS = { email: Mail, ticket: Ticket, task: ListTodo, service: Activity, contact: BookUser, kb: BookOpen, note: StickyNote };
const TINT = { email: "#3B82F6", ticket: "#8B5CF6", task: "#10B981", service: "#06B6D4", contact: "#F59E0B", kb: "#EC4899", note: "#EAB308" };

export default function GlobalSearch() {
  const navigate = useNavigate();
  const { selectedId } = useCompany();
  const [q, setQ] = useState("");
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const timer = useRef(null);

  const run = useCallback(async (term) => {
    if (term.trim().length < 2) { setGroups([]); setLoading(false); return; }
    setLoading(true);
    try {
      const cq = selectedId && selectedId !== "all" ? `&company_id=${selectedId}` : "";
      const { data } = await api.get(`/search?q=${encodeURIComponent(term)}${cq}`);
      setGroups(data.groups || []);
    } catch { setGroups([]); }
    finally { setLoading(false); }
  }, [selectedId]);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => run(q), 250);
    return () => clearTimeout(timer.current);
  }, [q, run]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); inputRef.current?.focus(); setOpen(true); }
      if (e.key === "Escape") { setOpen(false); inputRef.current?.blur(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (route) => { setOpen(false); setQ(""); navigate(route); };
  const total = groups.reduce((a, g) => a + g.items.length, 0);

  return (
    <div ref={wrapRef} className="relative flex-1 max-w-2xl mx-auto" data-testid="global-search">
      <div className={`flex items-center gap-2.5 h-11 px-4 rounded-full bg-white transition-all duration-200 ${open ? "shadow-[0_6px_28px_rgba(0,0,0,0.18)] ring-2 ring-klein/30" : "shadow-[0_1px_3px_rgba(0,0,0,0.12)] hover:shadow-[0_2px_10px_rgba(0,0,0,0.14)]"}`}>
        <Search size={17} className={open ? "text-klein" : "text-zinc-400"} />
        <input
          ref={inputRef}
          data-testid="global-search-input"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Rechercher dans FlowDesk…"
          className="flex-1 !bg-transparent !border-0 text-sm focus:outline-none placeholder:text-zinc-400 !text-zinc-900"
        />
        {loading && <Loader2 size={15} className="animate-spin text-zinc-400" />}
        {q && !loading && <button data-testid="global-search-clear" onClick={() => { setQ(""); inputRef.current?.focus(); }} className="text-zinc-400 hover:text-zinc-700"><X size={15} /></button>}
        {!q && <kbd className="hidden sm:inline text-[10px] font-mono text-zinc-400 border border-zinc-300 rounded px-1.5 py-0.5">⌘K</kbd>}
      </div>

      {open && q.trim().length >= 2 && (
        <div data-testid="global-search-results" className="absolute left-0 right-0 top-full mt-2 z-50 bg-card border border-black/10 dark:border-white/10 rounded-2xl shadow-2xl overflow-hidden max-h-[70vh] overflow-y-auto animate-in fade-in slide-in-from-top-1">
          {total === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-zinc-400">{loading ? "Recherche…" : `Aucun résultat pour « ${q} »`}</div>
          ) : groups.map((g) => {
            const Icon = ICONS[g.type] || Search;
            return (
              <div key={g.type} className="py-1.5">
                <div className="px-4 py-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500 flex items-center gap-2">{g.label} <span className="text-zinc-600">{g.items.length}</span></div>
                {g.items.map((it) => (
                  <button key={it.id} data-testid={`search-result-${it.id}`} onClick={() => pick(g.route)} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-black/[0.04] dark:hover:bg-white/[0.05] text-left group transition-colors">
                    <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: (TINT[g.type] || "#3B82F6") + "1a", color: TINT[g.type] || "#3B82F6" }}><Icon size={15} /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium truncate">{it.title}</span>
                      {it.subtitle && <span className="block text-xs text-zinc-500 truncate">{it.subtitle}</span>}
                    </span>
                    <CornerDownLeft size={14} className="text-zinc-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
